/**
 * Tiny stale-while-revalidate cache over the shared `api` axios instance.
 *
 * Solves the "every navigation refetches everything from scratch" UX
 * problem without pulling in SWR / React Query: each `useCached(url)`
 * call returns cached data synchronously on mount (if any) and quietly
 * refreshes in the background. In-flight requests for the same URL are
 * deduped across components, and the cache persists to sessionStorage
 * so a fresh tab still benefits within a session.
 *
 * Intended for navigation-static reads only (users, clients, templates,
 * gmail labels, etc.) — anything that changes frequently should keep
 * using `api.get()` directly with explicit refresh.
 *
 *   const { data, loading, refresh } = useCached("/users", { ttl: 60_000 });
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const MEM = new Map();        // url -> { data, ts }
const INFLIGHT = new Map();   // url -> Promise
const SUBSCRIBERS = new Map(); // url -> Set<setData>
const ERR_SUBSCRIBERS = new Map(); // url -> Set<setError>  (Iter 108b)
const SS_PREFIX = "cache:v1:";
const DEFAULT_TTL = 60_000;   // 60s — fresh window before background refresh

function _readSession(url) {
  try {
    const raw = sessionStorage.getItem(SS_PREFIX + url);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    return obj && typeof obj === "object" ? obj : null;
  } catch {
    return null;
  }
}

function _writeSession(url, data) {
  try {
    sessionStorage.setItem(
      SS_PREFIX + url,
      JSON.stringify({ data, ts: Date.now() })
    );
  } catch {
    // Quota or private-mode — best-effort only.
  }
}

function _broadcast(url, data) {
  const set = SUBSCRIBERS.get(url);
  if (!set) return;
  set.forEach((fn) => {
    try { fn(data); } catch { /* listener errored, skip */ }
  });
}

// Iter 108b — surface fetch errors to useCached subscribers so the
// UI can show a retry banner instead of silently rendering an empty
// list (the original failure mode that hid Gmail labels on prod).
function _broadcastError(url, err) {
  const set = ERR_SUBSCRIBERS.get(url);
  if (!set) return;
  set.forEach((fn) => {
    try { fn(err); } catch { /* listener errored, skip */ }
  });
}

/**
 * Fire-and-forget fetch that fills the cache. Safe to call from hover
 * handlers etc. Promise resolves with the data (or null on error).
 *
 * Iter 110e — Skip writing to the cross-tab sessionStorage cache when the
 * response looks "empty" for surfaces we know can't legitimately be empty
 * (e.g. Gmail labels — every Workspace mailbox has at least a dozen
 * system labels). A poisoned empty entry was leaving Triage's labels
 * sidebar blank on prod even though the live endpoint returned 25
 * labels on the next call. We still update the in-memory cache so the
 * round-trip dedup keeps working — only the persistent layer is
 * skipped when the payload smells stale.
 */
function _isEmptyShellResponse(url, data) {
  if (data == null) return true;
  if (url === "/gmail/labels") {
    if (!data || typeof data !== "object") return true;
    if (!Array.isArray(data.labels) || data.labels.length === 0) return true;
  }
  return false;
}

export async function prefetch(url) {
  if (!url) return null;
  if (INFLIGHT.has(url)) return INFLIGHT.get(url);
  const p = api
    .get(url)
    .then((r) => {
      const data = r.data;
      MEM.set(url, { data, ts: Date.now() });
      if (!_isEmptyShellResponse(url, data)) {
        _writeSession(url, data);
      }
      _broadcast(url, data);
      return data;
    })
    .catch((e) => {
      _broadcastError(url, e);
      return null;
    })
    .finally(() => {
      INFLIGHT.delete(url);
    });
  INFLIGHT.set(url, p);
  return p;
}

/**
 * Drop one URL or every cached URL. Use after mutations that invalidate
 * a list (e.g., creating a client → `invalidate("/clients")`).
 */
export function invalidate(url) {
  if (url == null) {
    MEM.clear();
    try { sessionStorage.clear(); } catch { /* ignore */ }
    return;
  }
  MEM.delete(url);
  try { sessionStorage.removeItem(SS_PREFIX + url); } catch { /* ignore */ }
}

export default function useCached(url, opts = {}) {
  const { ttl = DEFAULT_TTL, enabled = true } = opts;

  // Seed from in-memory first, then sessionStorage, so the first paint
  // never shows a spinner if we have any prior copy at all.
  //
  // Iter 110e — Reject empty-shell seeds (`/gmail/labels` with empty
  // labels array) so a poisoned cache from an older failed fetch can't
  // pin the UI to an empty state.
  const initial = (() => {
    if (!url || !enabled) return { data: null, fromCache: false };
    const m = MEM.get(url);
    if (m && !_isEmptyShellResponse(url, m.data)) return { data: m.data, fromCache: true, ts: m.ts };
    const s = _readSession(url);
    if (s && !_isEmptyShellResponse(url, s.data)) {
      MEM.set(url, { data: s.data, ts: s.ts });
      return { data: s.data, fromCache: true, ts: s.ts };
    }
    return { data: null, fromCache: false };
  })();

  const [data, setData] = useState(initial.data);
  const [loading, setLoading] = useState(initial.data == null);
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const fetchNow = useCallback(async () => {
    if (!url || !enabled) return;
    try {
      const fresh = await (INFLIGHT.get(url) || prefetch(url));
      if (mounted.current && fresh !== null && fresh !== undefined) {
        setData(fresh);
        setError(null);
      }
    } catch (e) {
      if (mounted.current) setError(e);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [url, enabled]);

  // Subscribe to other components fetching the same URL so this one
  // picks up their result without firing its own request.
  useEffect(() => {
    if (!url || !enabled) return undefined;
    if (!SUBSCRIBERS.has(url)) SUBSCRIBERS.set(url, new Set());
    if (!ERR_SUBSCRIBERS.has(url)) ERR_SUBSCRIBERS.set(url, new Set());
    const set = SUBSCRIBERS.get(url);
    const errSet = ERR_SUBSCRIBERS.get(url);
    const listener = (next) => {
      if (mounted.current) {
        setData(next);
        setError(null);
        setLoading(false);
      }
    };
    const errListener = (err) => {
      if (mounted.current) {
        setError(err);
        setLoading(false);
      }
    };
    set.add(listener);
    errSet.add(errListener);
    return () => {
      set.delete(listener);
      errSet.delete(errListener);
      if (set.size === 0) SUBSCRIBERS.delete(url);
      if (errSet.size === 0) ERR_SUBSCRIBERS.delete(url);
    };
  }, [url, enabled]);

  useEffect(() => {
    if (!url || !enabled) return;
    const cached = MEM.get(url);
    const stale = !cached || (Date.now() - cached.ts) > ttl;
    if (stale) fetchNow();
    else setLoading(false);
  }, [url, enabled, ttl, fetchNow]);

  const refresh = useCallback(() => {
    if (!url) return Promise.resolve(null);
    MEM.delete(url);
    setLoading(true);
    return fetchNow();
  }, [url, fetchNow]);

  return { data, loading, error, refresh, fromCache: initial.fromCache };
}
